import axios from "axios";

/**
 * Controllers for the lock-ups section.
 *
 * Two shapes. The grid still comes from the legacy universes route via
 * `controllers/holdings.getLockUps` — that endpoint carries the page's entitlement
 * gate and DataGrid reads its columns from the response, so there was nothing to
 * gain by moving it. Everything the page gained around the grid is here.
 */

const BASE = "/interface/api/lockups";

/** One horizon's worth of arriving supply. */
export interface LockUpWindow {
    days: number;
    expiries: number;
    companies: number;
    valueUsd: number | null;
    shares: number | null;
}

/** One week of the strip above the grid. */
export interface LockUpWeek {
    weekStart: string;
    count: number;
    valueUsd: number | null;
    shares: number | null;
}

/** An expiry the page leads with. */
export interface LockUpHighlight {
    name: string;
    ticker: string;
    exchange: string;
    stockId: number | null;
    securityId: number | null;
    expiryDate: string | null;
    daysUntil: number | null;
    sharesLocked: number | null;
    pctOfFreeFloat: number | null;
    daysOfVolume: number | null;
    valueUsd: number | null;
    siPctFreeFloat: number | null;
    securityClass: string;
}

export interface LockUpSummary {
    asOfDate: string;
    horizonDays: number;
    totalUpcoming: number;
    totalCompanies: number;
    countries: number;
    nextExpiryDate: string | null;
    windows: Record<string, LockUpWindow>;
    calendar: LockUpWeek[];
    highlights: LockUpHighlight[];
    coverage: { sized: number; significant: number; linked: number };
}

/** One lock-up as a stock page needs it. */
export interface StockLockUpEntry {
    expiryDate: string | null;
    daysUntil: number | null;
    isPast: boolean;
    sharesLocked: number | null;
    securityClass: string;
    lockupStartDate: string | null;
    lockupTermDays: number | null;
    pctOfFreeFloat: number | null;
    daysOfVolume: number | null;
    valueUsd: number | null;
    detail: string;
}

export interface StockLockUpStatus {
    companyId: number;
    hasUpcoming: boolean;
    next: StockLockUpEntry | null;
    upcoming: StockLockUpEntry[];
    recent: StockLockUpEntry[];
}

/** The stat band, week strip and highlight list above the lock-ups grid. */
export const getLockUpSummary = async (signal?: AbortSignal): Promise<LockUpSummary> => {
    const res = await axios.get<LockUpSummary>(`${BASE}/summary`, { signal });
    return res.data;
};

/**
 * Whether one company has shares coming free.
 *
 * Called on every stock overview, and most companies have no lock-up at all — so
 * the callout renders nothing until this resolves rather than reserving space for
 * a banner that will usually not appear.
 */
export const getStockLockUpStatus = async (companyId: number, signal?: AbortSignal): Promise<StockLockUpStatus> => {
    const res = await axios.get<StockLockUpStatus>(`${BASE}/company/${companyId}`, { signal });
    return res.data;
};
